import React, { useCallback, useMemo } from 'react'
import { FormProvider, UseFormReturn } from 'react-hook-form'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { Panel } from '@components/Panel/Panel'
import { mapToErrorMessage } from '@components/Web3/utils'
import { Box, FancyButton, FormRender, Icon, PopupMenu, Stack, Text } from '@ui'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useSpaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import {
    Address,
    SpaceAddressFromSpaceId,
    TransactionStatus,
    useMyLinkedWallets,
    useSanctionedAddresses,
} from 'use-towns-client'

import { FullPanelOverlay } from '../../WalletLinkingPanel'
import { WalletSelector } from '../WalletSelector'

import { TreasuryTransferSchema, treasuryTransferSchema } from './transferTreasurySchema'
import { useTransferTreasury } from './useTransferTreasury'
import { useRefetchTreasuryBalances, useTreasuryBalances } from './useTreasuryBalances'

export const TreasuryTransferPanel = React.memo(() => {
    const spaceId = useSpaceIdFromPathname()

    if (!spaceId) {
        return <></>
    }

    return <TreasuryTransferPanelContent spaceId={spaceId} />
})

function TreasuryTransferPanelContent(props: { spaceId: string }) {
    const { spaceId } = props
    const spaceAddress = SpaceAddressFromSpaceId(spaceId)
    const { data: linkedWallets, isLoading: isLinkedWalletsLoading } = useMyLinkedWallets()
    const aaAddress = useMyAbstractAccountAddress().data
    const treasuryBalances = useTreasuryBalances({ spaceAddress })

    return (
        <Panel label="Transfer Treasury" padding="none" data-testid="treasury-transfer-panel">
            {treasuryBalances.isLoading || isLinkedWalletsLoading ? (
                <Stack padding="md">
                    <ButtonSpinner />
                </Stack>
            ) : !treasuryBalances.hasAnyBalance ? (
                <Stack padding="lg">
                    <Text>There are no funds in the treasury.</Text>
                </Stack>
            ) : (
                <Stack padding height="100%">
                    <TreasuryTransferForm
                        spaceId={spaceId}
                        spaceAddress={spaceAddress}
                        treasuryBalances={treasuryBalances}
                        linkedWallets={linkedWallets}
                        aaAddress={aaAddress}
                    />
                </Stack>
            )}
        </Panel>
    )
}

type TreasuryTransferFormProps = {
    spaceAddress: Address
    spaceId: string
    treasuryBalances: ReturnType<typeof useTreasuryBalances>
    linkedWallets: string[] | undefined
    aaAddress: Address | undefined
}

function TreasuryTransferForm(props: TreasuryTransferFormProps) {
    const { spaceAddress, spaceId, treasuryBalances, linkedWallets, aaAddress } = props
    const { execute: transferTreasury, isLoading: isLoadingTransferTreasury } =
        useTransferTreasury()

    if (treasuryBalances.currencies.length === 0) {
        return null
    }

    return (
        <FormRender<TreasuryTransferSchema>
            gap
            schema={treasuryTransferSchema}
            defaultValues={{
                currency: treasuryBalances.currencies[0].address,
            }}
            id="TreasuryTransfer"
            mode="onChange"
            height="100%"
        >
            {(form: UseFormReturn<TreasuryTransferSchema>) => {
                return (
                    <FormProvider {...form}>
                        <CurrencySelector treasuryBalances={treasuryBalances} form={form} />
                        <Stack>
                            <Text strong>Send to</Text>
                            <WalletSelector
                                aaAddress={aaAddress}
                                linkedWallets={linkedWallets}
                                fromWallet={spaceAddress}
                            />
                        </Stack>

                        <SubmitButton
                            form={form}
                            treasuryBalances={treasuryBalances}
                            spaceId={spaceId}
                            transferTreasury={transferTreasury}
                        />

                        {isLoadingTransferTreasury && <FullPanelOverlay withSpinner />}
                    </FormProvider>
                )
            }}
        </FormRender>
    )
}

function SingleCurrencyDisplay(props: { symbol: 'ETH' | 'USDC'; formattedValue: string }) {
    const { symbol, formattedValue } = props

    return (
        <Box horizontal alignItems="center" gap="sm">
            <Icon
                type={symbol === 'ETH' ? 'base' : 'usdc'}
                color={symbol === 'ETH' ? 'coinbaseBlue' : 'default'}
            />
            <Box gap="xs">
                <Text strong>
                    {formattedValue} {symbol}
                </Text>
                <Text size="sm" color="gray2">
                    Base
                </Text>
            </Box>
        </Box>
    )
}

function CurrencySelector(props: {
    treasuryBalances: ReturnType<typeof useTreasuryBalances>
    form: UseFormReturn<TreasuryTransferSchema>
}) {
    const { treasuryBalances, form } = props
    const currencies = treasuryBalances.currencies
    const selectedAddress = form.watch('currency')
    const selectedCurrency = currencies.find((c) => c.address === selectedAddress) ?? currencies[0]
    return (
        <Box gap="md">
            <Text strong>Asset</Text>
            {treasuryBalances.currencies.length === 1 ? (
                <Box padding="md" background="level2" rounded="sm">
                    <SingleCurrencyDisplay
                        symbol={treasuryBalances.currencies[0].symbol}
                        formattedValue={treasuryBalances.currencies[0].formatted}
                    />
                </Box>
            ) : (
                <PopupMenu
                    options={currencies}
                    value={selectedCurrency}
                    renderButton={(option) => (
                        <Box horizontal justifyContent="spaceBetween">
                            <SingleCurrencyDisplay
                                symbol={option.symbol}
                                formattedValue={option.formatted}
                            />
                            <Icon color="gray2" type="down" />
                        </Box>
                    )}
                    renderOption={(option) => (
                        <SingleCurrencyDisplay
                            symbol={option.symbol}
                            formattedValue={option.formatted}
                        />
                    )}
                    getKey={(option) => option.address}
                    onChange={(option) => form.setValue('currency', option.address)}
                />
            )}
        </Box>
    )
}

function SubmitButton(props: {
    form: UseFormReturn<TreasuryTransferSchema>
    treasuryBalances: ReturnType<typeof useTreasuryBalances>
    spaceId: string
    transferTreasury: ReturnType<typeof useTransferTreasury>['execute']
}) {
    const { form, spaceId, transferTreasury } = props

    const { isSanctioned } = useSanctionedAddresses()
    const currency = form.watch('currency')
    const recipient = form.watch('recipient')
    const spaceAddress = SpaceAddressFromSpaceId(spaceId)
    const refetchTreasuryBalances = useRefetchTreasuryBalances()

    const isDisabled = useMemo(() => {
        return form.formState.isSubmitting || !currency || !recipient
    }, [form.formState.isSubmitting, currency, recipient])

    const onValid = useCallback(
        async (data: TreasuryTransferSchema, getSigner: GetSigner) => {
            const signer = await getSigner()
            if (!signer) {
                createPrivyNotAuthenticatedNotification()
                return
            }
            if (isSanctioned(recipient?.toLowerCase() || '')) {
                form.setError('recipient', {
                    type: 'manual',
                    message: "This wallet has been blocked by OFAC's Sanctions List",
                })
                popupToast(({ toast }) => (
                    <StandardToast.Error
                        toast={toast}
                        message="This wallet has been blocked by OFAC's Sanctions List"
                    />
                ))
                return
            }
            if (!data.recipient) {
                form.setError('recipient', {
                    type: 'manual',
                    message: 'Recipient is required',
                })
                return
            }

            const result = await transferTreasury({
                data: {
                    spaceId,
                    recipient: data.recipient,
                    currencyAddress: data.currency,
                },
                signer,
            })

            if (result?.status === TransactionStatus.Success) {
                await refetchTreasuryBalances(spaceAddress)
                popupToast(({ toast }) => (
                    <StandardToast.Success
                        toast={toast}
                        message="Treasury transferred successfully"
                    />
                ))
            } else {
                popupToast(({ toast }) => (
                    <StandardToast.Error
                        toast={toast}
                        message="Failed to transfer treasury"
                        subMessage={mapToErrorMessage({
                            error: result?.error,
                            source: 'transferTreasury',
                        })}
                    />
                ))
            }
        },
        [
            isSanctioned,
            recipient,
            transferTreasury,
            spaceId,
            form,
            refetchTreasuryBalances,
            spaceAddress,
        ],
    )

    return (
        <WalletReady>
            {({ getSigner }) => (
                <>
                    <Stack grow />
                    <FancyButton
                        borderRadius="lg"
                        cta={!isDisabled}
                        data-testid="submit-button"
                        disabled={isDisabled}
                        onClick={form.handleSubmit((data) => onValid(data, getSigner))}
                    >
                        Next
                    </FancyButton>
                </>
            )}
        </WalletReady>
    )
}
